/**
 * Favorites — store the jobs a visitor has "liked" in a cookie, as an array
 * of job ids. Reads/writes go through cookies.js. DOM wiring uses only data
 * attributes (no classes, no ids) to find triggers and job ids, and to
 * expose state back to the markup.
 */

import { getCookie, setCookie } from './cookies.js';
import Modal from './modal.js';

export const COOKIE_FAVORITES = 'proindustry_favorites';

const COOKIE_EXPIRES_DAYS = 365;
const TRIGGER_SELECTOR = '[data-action="add-favorite"]';
const JOB_ID_ATTRIBUTES = ['data-jobid', 'data-job-id', 'data-vacancyid'];

/**
 * Read the favorite job ids from the cookie.
 *
 * @returns {string[]}
 */
export function getFavorites() {
    const raw = getCookie(COOKIE_FAVORITES);

    if (!raw) {
        return [];
    }

    try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed?.ids) ? parsed.ids.map(String) : [];
    } catch {
        return [];
    }
}

/**
 * Persist the favorite job ids to the cookie.
 *
 * @param {string[]} ids
 */
function saveFavorites(ids) {
    setCookie(COOKIE_FAVORITES, JSON.stringify({ ids }), {
        expires: COOKIE_EXPIRES_DAYS
    });
}

/**
 * Whether a job id is currently a favorite.
 *
 * @param {string|number} jobId
 * @returns {boolean}
 */
export function isFavorite(jobId) {
    return getFavorites().includes(String(jobId));
}

/**
 * Add a job id to the favorites.
 *
 * @param {string|number} jobId
 */
export function addFavorite(jobId) {
    const id = String(jobId);
    const ids = getFavorites();

    if (!ids.includes(id)) {
        saveFavorites([...ids, id]);
    }
}

/**
 * Remove a job id from the favorites.
 *
 * @param {string|number} jobId
 */
export function removeFavorite(jobId) {
    saveFavorites(getFavorites().filter((id) => id !== String(jobId)));
}

/**
 * Toggle a job id in/out of the favorites.
 *
 * @param {string|number} jobId
 * @returns {boolean} the new favorite state
 */
export function toggleFavorite(jobId) {
    const nowFavorite = !isFavorite(jobId);
    nowFavorite ? addFavorite(jobId) : removeFavorite(jobId);

    return nowFavorite;
}

/**

 * Clear all stored favorite data from local/session storage, cookies, and UI.
 */
export function removeAllFavorites() {
    const storageKeys = ['favorites', 'favorite_jobs', 'favoriteJobs', 'proindustry_favorites'];

    try {
        storageKeys.forEach((key) => {
            if (window.localStorage) {
                window.localStorage.removeItem(key);
            }
            if (window.sessionStorage) {
                window.sessionStorage.removeItem(key);
            }
        });
    } catch (error) {
        console.warn('Could not clear favorite storage:', error);
    }

    storageKeys.forEach((key) => {
        document.cookie = `${key}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax`;
    });

    const favoriteCounter = document.querySelector('[data-favorites-counter]');
    if (favoriteCounter) {
        favoriteCounter.textContent = '0';
    }

    document.querySelectorAll('.btn--like, [data-action="add-favorite"], [data-isfavorite="true"], [data-action="remove-favorite"]').forEach((element) => {
        element.classList.remove('active');
        element.classList.add('inactive');
        element.setAttribute('aria-pressed', 'false');
    });

    document.dispatchEvent(new CustomEvent('favorites:updated', { detail: { count: 0 } }));
    window.location.reload();
}

/**
 * Sync the favorite state for all triggers and the header counter from the
 * current cookie value.
 */
function syncFavoriteUI() {
    const favoriteIds = new Set(getFavorites());

    document.querySelectorAll(TRIGGER_SELECTOR).forEach((trigger) => {
        const jobId = resolveJobId(trigger);

        if (!jobId) {
            return;
        }

        setTriggerState(trigger, favoriteIds.has(String(jobId)));
    });

    document.querySelectorAll('[data-favorites-counter]').forEach((counter) => {
        counter.textContent = String(favoriteIds.size);
    });

    document.querySelectorAll('.site-header .btn--like').forEach((link) => {
        const hasFavorites = favoriteIds.size > 0;
        link.classList.toggle('active', hasFavorites);
        link.classList.toggle('inactive', !hasFavorites);
    });
}

/**
 * Resolve the job id linked to a favorite trigger: either on the trigger
 * itself or on the closest ancestor carrying a known job id attribute.
 *
 * @param {HTMLElement} trigger
 * @returns {string|null}
 */
function resolveJobId(trigger) {
    for (const attribute of JOB_ID_ATTRIBUTES) {
        const directValue = trigger.getAttribute(attribute);
        const ancestorValue = trigger.closest(`[${attribute}]`)?.getAttribute(attribute);
        const value = directValue ?? ancestorValue;

        if (value) {
            return value;
        }
    }

    return null;
}

/**
 * Reflect the favorite state on a trigger via a data attribute only.
 *
 * @param {HTMLElement} trigger
 * @param {boolean} favorite
 */
function setTriggerState(trigger, favorite) {
    trigger.setAttribute('data-isfavorite', favorite ? 'yes' : 'no');
    trigger.setAttribute('aria-pressed', favorite ? 'true' : 'false');
    trigger.classList.toggle('active', favorite);
    trigger.classList.toggle('inactive', !favorite);
}

/**
 * Read the confirmation title/content for the new favorite state from the
 * trigger's data-like / data-unlike attributes.
 *
 * @param {HTMLElement} trigger
 * @param {boolean} favorite
 * @returns {{title: string, content: string}|null}
 */
function getConfirmationText(trigger, favorite) {
    const attribute = favorite ? 'data-like' : 'data-unlike';
    const raw = trigger.getAttribute(attribute);

    if (!raw) {
        return null;
    }

    try {
        return JSON.parse(raw);
    } catch {
        return null;
    }
}

/**
 * Show the like/unlike confirmation modal, if the trigger provides text.
 *
 * @param {HTMLElement} trigger
 * @param {boolean} favorite
 */
function showConfirmationModal(trigger, favorite) {
    const text = getConfirmationText(trigger, favorite);

    if (!text) {
        return;
    }

    new Modal(text.title, text.content);
}

/**
 * Wire up every current and future favorite trigger on the page via a
 * single delegated click listener.
 */
export function initFavorites() {
    syncFavoriteUI();

    document.addEventListener('click', (event) => {
        const removeAllTrigger = event.target.closest('[data-action="removeallfavorites"]');

        if (removeAllTrigger) {
            event.preventDefault();
            removeAllFavorites();
            return;
        }

        const trigger = event.target.closest(TRIGGER_SELECTOR);

        if (!trigger) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();

        const jobId = resolveJobId(trigger);

        if (!jobId) {
            return;
        }

        const favorite = toggleFavorite(jobId);
        syncFavoriteUI();
        showConfirmationModal(trigger, favorite);

        trigger.dispatchEvent(
            new CustomEvent('favorites:change', {
                bubbles: true,
                detail: { jobId, favorite }
            })
        );
    });
}